<template>
  <div class="mobile-chat">
    <!-- 顶部导航栏 -->
    <header class="chat-header">
      <div class="header-content">
        <img src="/favicon.png" alt="logo" class="header-logo" />
        <h1 class="header-title">鄞州区基础党务小助手</h1>
        <button class="menu-btn" @click="toggleMenu">☰</button>
      </div>
      
      <!-- 功能菜单 -->
      <div v-if="showMenu" class="menu-dropdown">
        <div class="menu-item" @click="logout">退出登录</div>
      </div>
    </header>

    <!-- 对话内容区 -->
    <main class="chat-content" ref="messageContainer" @scroll="handleScroll">
      <!-- 加载更多历史记录提示 -->
      <div v-if="isLoadingMore" class="loading-more">
        <div class="loading-spinner"></div>
        <span>正在加载更多历史记录...</span>
      </div>

      <!-- 历史记录加载提示 -->
      <div v-if="isLoadingHistory" class="loading-history">
        <div class="loading-spinner"></div>
        <span>正在加载历史对话记录...</span>
      </div>

      <!-- 无历史记录提示 -->
      <div v-if="!isLoadingHistory && messages.length === 0" class="no-messages">
        <div class="empty-state">
          <p class="empty-text">暂无对话记录</p>
          <p class="empty-subtext">开始对话后，您的聊天记录将自动保存</p>
        </div>
      </div>
      
      <!-- 对话消息 -->
      <div v-for="(message, index) in messages" 
           :key="index"
           :class="['message-bubble', message.type + '-message']">
        <div v-if="message.type === 'left'" v-html="renderMarkdown(message.content)"></div>
        <p v-else>{{ message.content }}</p>
      </div>
    </main>

    <!-- 底部输入区 -->
    <footer class="chat-input">
      <textarea 
        v-model="inputMessage"
        @keydown.enter.prevent="handleEnterKey"
        :disabled="!isConnected || isWriting"
        placeholder="请输入您的问题..."
        class="message-input"
      ></textarea>
      <button 
        @click="sendMessage"
        :disabled="!isConnected || isWriting || !inputMessage.trim()"
        class="send-button"
      >
        <span v-if="!isWriting">发送</span>
        <div v-else class="sending-spinner">
          <div class="spinner"></div>
        </div>
      </button>
    </footer>

    <!-- 连接状态提示 -->
    <div v-if="!isConnected" class="connection-status">
      连接已断开，正在重新连接...
    </div>

    <!-- 错误消息提示 -->
    <div v-if="errorMessage" class="error-message">
      {{ errorMessage }}
      <button class="error-close" @click="errorMessage = ''">×</button>
    </div>
  </div>
</template>

<script>
import SockJS from 'sockjs-client';
import markdownit from 'markdown-it';

export default {
  name: 'MobileChat',
  data() {
    return {
      ws: null,
      messages: [],
      inputMessage: '',
      isConnected: false,
      isWriting: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      md: null,
      userId: null,
      isLoadingHistory: false,
      isLoadingMore: false,
      hasMoreHistory: true,
      currentPage: 0,
      pageSize: 5,
      autoScrollToBottom: true,
      showMenu: false,
      errorMessage: '',
      tokenValidationTimer: null,
      tokenValidationInterval: 5 * 60 * 1000,
      singleLoginCheckTimer: null,
      singleLoginCheckInterval: 10 * 1000
    };
  },
  
  async mounted() {
    this.checkAuthStatus();
    this.startTokenValidation();
    this.startSingleLoginCheck();
    
    // 初始化markdown
    this.md = new markdownit({
      html: true,
      xhtmlOut: true,
      breaks: false,
      langPrefix: 'language-',
      linkify: true,
      typographer: true,
      quotes: '""\'\''
    });
    
    this.initWebSocket();
    await this.loadChatHistory();
    
    // 添加事件监听
    document.addEventListener('click', this.handleClickOutside);
  },
  
  beforeDestroy() {
    this.closeWebSocket();
    this.stopTokenValidation();
    this.stopSingleLoginCheck();
    document.removeEventListener('click', this.handleClickOutside);
  },
  
  methods: {
    // 检查登录状态
    checkAuthStatus() {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      const displayName = localStorage.getItem('displayName');
      
      if (!token || !sessionId || !displayName) {
        this.$router.push('/login');
        return;
      }
    },
    
    // 启动定期token验证
    startTokenValidation() {
      this.tokenValidationTimer = setInterval(() => {
        this.validateToken();
      }, this.tokenValidationInterval);
    },
    
    // 停止token验证
    stopTokenValidation() {
      if (this.tokenValidationTimer) {
        clearInterval(this.tokenValidationTimer);
        this.tokenValidationTimer = null;
      }
    },

    // 启动单点登录状态检查
    startSingleLoginCheck() {
      this.singleLoginCheckTimer = setInterval(() => {
        this.checkSingleLoginStatus();
      }, this.singleLoginCheckInterval);
    },

    // 停止单点登录状态检查
    stopSingleLoginCheck() {
      if (this.singleLoginCheckTimer) {
        clearInterval(this.singleLoginCheckTimer);
        this.singleLoginCheckTimer = null;
      }
    },

    // 检查单点登录状态
    async checkSingleLoginStatus() {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/auth/check-single-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        });

        const result = await response.json();
        
        if (result.code === 200 && result.isLoggedInElsewhere) {
          this.handleAuthFailure('您的账户已在其他地方登录');
        }
      } catch (error) {
        console.error('检查单点登录状态失败:', error);
      }
    },
    
    // 验证token有效性
    async validateToken() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.handleAuthFailure('Token不存在');
          return;
        }
        
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/auth/validate-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': token
          }
        });
        
        const result = await response.json();
        
        if (result.code !== 200 || !result.valid) {
          this.handleAuthFailure(result.message || 'Token验证失败');
        }
      } catch (error) {
        console.error('Token验证失败:', error);
        this.handleAuthFailure('网络错误，请重新登录');
      }
    },
    
    // 处理认证失败
    handleAuthFailure(message) {
      localStorage.clear();
      alert(`认证失败：${message}，即将跳转到登录页面`);
      this.$router.push('/login');
    },

    // 处理Enter键事件
    handleEnterKey(event) {
      if (event.shiftKey) return;
      this.sendMessage();
    },

    // 初始化WebSocket连接
    initWebSocket() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.showError('用户未登录，请先登录');
          return;
        }
        
        const wsUrl = `${process.env.VUE_APP_WS_BASE_URL}/chat?token=${encodeURIComponent(token)}`;
        const sockjs = new SockJS(wsUrl);
        this.ws = sockjs;
        
        this.ws.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
        };
        
        this.ws.onmessage = (event) => {
          if (event.data === '---====:::End') {
            this.isWriting = false;
            return;
          }
          
          try {
            const response = JSON.parse(event.data);
            if (response.error) {
              this.showError(response.error);
              this.isWriting = false;
            } else {
              setTimeout(() => {
                this.appendMessage('left', response.content || event.data);
              }, 1200);
            }
          } catch (e) {
            this.appendMessage('left', event.data);
          }
        };
        
        this.ws.onerror = (error) => {
          this.isConnected = false;
          this.showError('连接发生错误，正在尝试重新连接...');
          this.isWriting = false;
        };
        
        this.ws.onclose = () => {
          this.isConnected = false;
          this.reconnect();
        };
      } catch (error) {
        this.isConnected = false;
        this.showError('连接初始化失败，请刷新页面重试');
      }
    },

    // 关闭WebSocket连接
    closeWebSocket() {
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
    },
    
    // 重新连接
    reconnect() {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => {
          this.initWebSocket();
        }, 3000);
      } else {
        this.showError('连接失败，请刷新页面重试');
      }
    },

    // 发送消息
    sendMessage() {
      if (this.inputMessage.trim() && this.isConnected) {
        this.isWriting = true;
        try {
          this.ws.send(this.inputMessage);
          this.appendMessage('right', this.inputMessage);
          this.inputMessage = '';
        } catch (error) {
          this.isConnected = false;
          this.showError('发送消息失败，请检查网络连接');
        }
      }
    },

    // 渲染Markdown内容
    renderMarkdown(content) {
      return this.md.render(content);
    },

    // 过滤特殊字符
    filterSpecialChars(content) {
      if (typeof content !== 'string') return content;
      // 过滤 ##数字$$ 格式的特殊字符
      return content.replace(/##\d+\$\$/g, '');
    },

    // Unicode解码方法
    decodeUnicode(content) {
      if (typeof content !== 'string') return content;
      
      // 解码Unicode格式的字符串
      // 优先处理 \uXXXX 格式，如果不存在则处理 uXXXX 格式
      if (content.includes('\\u')) {
        // 如果包含 \uXXXX 格式，使用这种格式解码
        return content.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
      } else if (content.includes('u') && /u[0-9a-fA-F]{4}/.test(content)) {
        // 如果包含 uXXXX 格式，使用这种格式解码
        return content.replace(/u([0-9a-fA-F]{4})/g, (match, hex) => {
          return String.fromCharCode(parseInt(hex, 16));
        });
      }
      
      // 如果都不包含，返回原内容
      return content;
    },

    // 流式处理函数
    processStream(dataArray) {
      // 过滤特殊字符
      const filteredData = this.filterSpecialChars(dataArray);
      
      // 合并新内容（避免重复）
      let currentText = this.messages[this.messages.length - 1].content;
      const newText = filteredData.replace(currentText, '');
      if (newText.length > 0) {
        currentText += newText;
        if (this.messages.length > 0) {
          this.messages[this.messages.length - 1].content = currentText;
          
          // 流式更新时，如果启用了自动滚动，则滚动到底部
          if (this.autoScrollToBottom) {
            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        }
      }
    },

    // 添加消息到列表
    appendMessage(type, content) {
      if (type === 'right') {
        this.messages.push({ type, content });
        this.messages.push({ type: 'left', content: '' });
        this.autoScrollToBottom = true;
      } else if (type === 'left') {
        // AI回答...
        this.processStream(content);
        // AI回答时，启用自动滚动
        this.autoScrollToBottom = true;
      } else {
        this.messages.push({ type, content });
      }
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      });
    },
    
    // 获取当前用户ID
    getCurrentUserId() {
      try {
        const userId = localStorage.getItem('userId');
        if (userId) return parseInt(userId);
        
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload.userId || payload.sub || null;
        }
      } catch (error) {
        console.error('获取用户ID失败:', error);
      }
      return null;
    },
    
    // 加载聊天历史记录
    async loadChatHistory() {
      try {
        this.userId = this.getCurrentUserId();
        if (!this.userId) return;

        this.isLoadingHistory = true;
        this.currentPage = 0;

        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/user/${this.userId}/history`);
        const result = await response.json();

        if (result.code === 200 && result.data && result.data.length > 0) {
          this.messages = [];
          
          result.data.forEach(message => {
            if (message.messageType === 'QUESTION') {
              this.messages.push({
                type: 'right',
                content: this.decodeUnicode(message.content)
              });
            } else if (message.messageType === 'ANSWER') {
              this.messages.push({
                type: 'left',
                content: this.decodeUnicode(message.content)
              });
            }
          });

          this.hasMoreHistory = result.data.length >= (this.pageSize * 2);
          
          this.$nextTick(() => {
            setTimeout(() => {
              this.scrollToBottom();
            }, 100);
          });
        } else {
          this.hasMoreHistory = false;
        }
      } catch (error) {
        console.error('加载历史记录失败:', error);
        this.hasMoreHistory = false;
        this.showError('加载历史记录失败');
      } finally {
        this.isLoadingHistory = false;
      }
    },
    
    // 加载更多历史记录
    async loadMoreHistory() {
      if (this.isLoadingMore || !this.hasMoreHistory) return;

      try {
        this.isLoadingMore = true;
        this.currentPage++;
        
        const sessionId = localStorage.getItem('sessionId') || 'default';
        const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/api/chat-messages/load-more?userId=${this.userId}&sessionId=${sessionId}&page=${this.currentPage}&size=${this.pageSize}`);
        const result = await response.json();

        if (result.code === 200 && result.data && result.data.length > 0) {
          const newMessages = [];
          result.data.forEach(message => {
            if (message.messageType === 'QUESTION') {
              newMessages.push({
                type: 'right',
                content: this.decodeUnicode(message.content)
              });
            } else if (message.messageType === 'ANSWER') {
              newMessages.push({
                type: 'left',
                content: this.decodeUnicode(message.content)
              });
            }
          });

          this.messages.unshift(...newMessages);
          
          this.hasMoreHistory = result.data.length >= (this.pageSize * 2);
        } else {
          this.hasMoreHistory = false;
        }
      } catch (error) {
        console.error('加载更多历史记录失败:', error);
        this.hasMoreHistory = false;
        this.showError('加载更多历史记录失败');
      } finally {
        this.isLoadingMore = false;
      }
    },
    
    // 处理滚动事件
    handleScroll(event) {
      const container = event.target;
      const scrollTop = container.scrollTop;
      
      if (scrollTop <= 100 && this.hasMoreHistory && !this.isLoadingMore) {
        this.loadMoreHistory();
      }
    },

    // 滚动到底部
    scrollToBottom() {
      if (this.autoScrollToBottom) {
        const container = this.$refs.messageContainer;
        if (container) {
          this.$nextTick(() => {
            container.scrollTop = container.scrollHeight;
          });
        }
      }
    },
    
    // 显示错误消息
    showError(message) {
      this.errorMessage = message;
      setTimeout(() => {
        this.errorMessage = '';
      }, 3000);
    },
    
    // 切换菜单显示
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },

    // 退出登录
    logout() {
      localStorage.clear();
      this.$router.push('/login');
    },

    // 处理点击外部区域关闭菜单
    handleClickOutside(event) {
      if (!this.showMenu) return;
      
      const menu = this.$el.querySelector('.menu-dropdown');
      const menuBtn = this.$el.querySelector('.menu-btn');
      
      if (menu && !menu.contains(event.target) && menuBtn && !menuBtn.contains(event.target)) {
        this.showMenu = false;
      }
    }
  }
};
</script>

<style scoped>
/* 基础样式重置 */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* 主容器 */
.mobile-chat {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  overflow: hidden;
}

/* 顶部导航栏 */
.chat-header {
  flex-shrink: 0;
  background-color: #c03333;
  color: white;
  padding: 12px 16px;
  padding-top: calc(12px + env(safe-area-inset-top, 0px));
  position: relative;
  z-index: 100;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-logo {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.header-title {
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  margin: 0 12px;
  text-align: center;
}

.menu-btn {
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* 菜单下拉 */
.menu-dropdown {
  position: absolute;
  top: 100%;
  right: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 101;
}

.menu-item {
  padding: 12px 16px;
  cursor: pointer;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
}

.menu-item:hover {
  background-color: #f5f5f5;
}

.menu-item:last-child {
  border-bottom: none;
}

/* 对话内容区 */
.chat-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background-color: white;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* 滚动条样式 */
.chat-content::-webkit-scrollbar {
  width: 6px;
}

.chat-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-content::-webkit-scrollbar-thumb {
  background: #c03333;
  border-radius: 3px;
  min-height: 40px;
}

.chat-content::-webkit-scrollbar-thumb:hover {
  background: #a62c2c;
}

/* 加载提示 */
.loading-more,
.loading-history {
  text-align: center;
  padding: 16px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  font-size: 14px;
  color: #666;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #c03333;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 无消息状态 */
.no-messages {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  text-align: center;
}

.empty-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.empty-subtext {
  font-size: 14px;
  color: #999;
}

/* 消息气泡 */
.message-bubble {
  margin-bottom: 16px;
  max-width: 100%;
  word-break: break-word;
  line-height: 1.5;
}

.right-message {
  background-color: #c03333;
  color: white;
  padding: 12px 16px;
  border-radius: 18px;
  margin-left: 20%;
  margin-right: 0;
}

.left-message {
  background-color: white;
  color: #333;
  padding: 12px 16px;
  border-radius: 18px;
  margin-right: 20%;
  margin-left: 0;
  border: 1px solid #e0e0e0;
}

/* 底部输入区 */
.chat-input {
  flex-shrink: 0;
  background-color: white;
  border-top: 1px solid #e0e0e0;
  padding: 12px 16px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  display: flex;
  align-items: flex-end;
  gap: 12px;
}

.message-input {
  flex: 1;
  min-height: 40px;
  max-height: 120px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  resize: none;
  font-size: 16px;
  line-height: 1.4;
  outline: none;
  font-family: inherit;
}

.message-input:focus {
  border-color: #c03333;
}

.message-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.send-button {
  padding: 0 20px;
  height: 40px;
  background-color: #c03333;
  color: white;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-button:hover:not(:disabled) {
  background-color: #a62c2c;
}

.send-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* 发送中动画 */
.sending-spinner {
  width: 20px;
  height: 20px;
  position: relative;
}

.spinner {
  width: 100%;
  height: 100%;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* 连接状态 */
.connection-status {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #f56c6c;
  color: white;
  padding: 8px 16px;
  border-radius: 16px;
  font-size: 14px;
  z-index: 1000;
}

/* 错误消息 */
.error-message {
  position: fixed;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #f56c6c;
  color: white;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 14px;
  z-index: 1000;
  max-width: 80%;
  text-align: center;
  display: flex;
  align-items: center;
  gap: 10px;
}

.error-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

/* Markdown内容样式 */
.left-message :deep(h1),
.left-message :deep(h2),
.left-message :deep(h3),
.left-message :deep(h4),
.left-message :deep(h5),
.left-message :deep(h6) {
  margin: 8px 0;
  font-weight: 600;
}

.left-message :deep(p) {
  margin: 8px 0;
  line-height: 1.6;
}

.left-message :deep(code) {
  background-color: #f3f3f3;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: Consolas, Monaco, 'Andale Mono', monospace;
}

.left-message :deep(pre) {
  background-color: #f6f8fa;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.left-message :deep(pre code) {
  background-color: transparent;
  padding: 0;
}

.left-message :deep(ul),
.left-message :deep(ol) {
  padding-left: 20px;
  margin: 8px 0;
}

.left-message :deep(blockquote) {
  margin: 8px 0;
  padding-left: 10px;
  border-left: 4px solid #dfe2e5;
  color: #6a737d;
}

.left-message :deep(a) {
  color: #c03333;
  text-decoration: none;
}

.left-message :deep(a:hover) {
  text-decoration: underline;
}

.left-message :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.left-message :deep(th),
.left-message :deep(td) {
  border: 1px solid #dfe2e5;
  padding: 8px;
  text-align: left;
}

.left-message :deep(th) {
  background-color: #f6f8fa;
  font-weight: 600;
}

.left-message :deep(hr) {
  border: none;
  border-top: 1px solid #dfe2e5;
  margin: 16px 0;
}

.left-message :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.left-message :deep(strong) {
  font-weight: 600;
}

.left-message :deep(em) {
  font-style: italic;
}

.left-message :deep(del) {
  text-decoration: line-through;
}

/* 移动端优化 */
@media (max-width: 768px) {
  .chat-content {
    padding: 12px;
  }
  
  .message-input {
    font-size: 16px !important;
  }
  
  .right-message {
    margin-left: 10%;
  }
  
  .left-message {
    margin-right: 10%;
  }
}

/* iPhone 安全区域支持 */
@supports (padding: max(0px)) {
  .chat-header {
    padding-top: max(12px, env(safe-area-inset-top));
  }
  
  .chat-input {
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
}
</style>